import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
  IconButton, FormControl, InputLabel, OutlinedInput, Checkbox, ListItemText, Switch, Alert, Chip, Stack
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { bookingsApi, seriesApi, usersApi } from "./api";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [series, setSeries] = useState([]);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingUser, setBookingUser] = useState("");
  const [datum, setDatum] = useState("");
  const [hour, setHour] = useState("");
  const [platzNummer, setPlatzNummer] = useState(1);

  // Serientermin Dialog
  const [serienDialogOpen, setSerienDialogOpen] = useState(false);
  const [serieName, setSerieName] = useState("");
  const [seriePlatzNummer, setSeriePlatzNummer] = useState(1);
  const [serieWeekday, setSerieWeekday] = useState(1);
  const [serieHours, setSerieHours] = useState([]);
  const [serieFromDate, setSerieFromDate] = useState("");
  const [serieToDate, setSerieToDate] = useState("");
  const [serieUserIds, setSerieUserIds] = useState([]);

  const [trigger, setTrigger] = useState(0);
  const [showDashboard, setShowDashboard] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [userMessage, setUserMessage] = useState("");

  useEffect(() => {
    Promise.all([usersApi.list(), bookingsApi.list(), seriesApi.list()]).then(([u, b, s]) => {
      setUsers(u.users || u); setBookings(b.bookings || b); setSeries(s.series || s);
    });
  }, [trigger]);

  // User approve/delete/admin
  const handleApprove = async (userId) => {
    await usersApi.update(userId, { approved: true });
    setTrigger(t => t + 1);
  };
  const handleSetActive = async (userId, active) => {
    await usersApi.update(userId, { active });
    setTrigger(t => t + 1);
  };
  const handleSeasonReset = async () => {
    if (!window.confirm("Neue Saison starten?\n\nAlle Mitglieder (außer Admins) werden auf „inaktiv“ gesetzt und können erst wieder buchen, wenn du sie nach Einzahlung freischaltest.")) return;
    const { count } = await usersApi.seasonReset();
    setUserMessage(`${count} Mitglied(er) wurden für die neue Saison inaktiv gesetzt.`);
    setTrigger(t => t + 1);
  };
  const handleResetLink = async (user) => {
    const { link, expiresInHours } = await usersApi.createPasswordResetLink(user.id);
    setLinkCopied(false);
    setResetLink({ user, link, expiresInHours });
  };
  const copyResetLink = async () => {
    try { await navigator.clipboard.writeText(resetLink.link); setLinkCopied(true); } catch { setLinkCopied(false); }
  };
  const handleDeleteUser = async (userId) => {
    await usersApi.remove(userId);
    setTrigger(t => t + 1);
  };
  const handleSetAdmin = async (userId, isAdmin) => {
    await usersApi.update(userId, { isAdmin });
    setTrigger(t => t + 1);
  };
  const handleSetGender = async (userId, gender) => {
    await usersApi.update(userId, { gender });
    setTrigger(t => t + 1);
  };

  const handleDeleteBooking = async (bookingId) => {
    await bookingsApi.remove(bookingId);
    setTrigger(t => t + 1);
  };
  const openBookingDlg = () => {
    setDatum(""); setHour(""); setPlatzNummer(1); setBookingUser("");
    setBookingDialogOpen(true);
  };
  const handleCreateBooking = async () => {
    if (!bookingUser || !datum || !hour || !platzNummer) return;
    const alreadyBooked = bookings.some(b => b.date === datum && b.hour === Number(hour) && b.platzNummer === platzNummer)
      || series.some(s => s.place === platzNummer &&
          ((Array.isArray(s.hours) ? s.hours.includes(Number(hour)) : s.hour === Number(hour)))
          && dayjs(datum).isBetween(s.fromDate, s.toDate, null, "[]") && s.weekday === dayjs(datum).day());
    if (alreadyBooked) {
      alert("Dieser Platz ist in dieser Stunde bereits gebucht.");
      return;
    }
    await bookingsApi.create({
      userId: bookingUser,
      date: datum,
      hour: Number(hour),
      platzNummer: platzNummer
    });
    setBookingDialogOpen(false);
    setTrigger(t => t + 1);
  };

  // Serientermin add/delete
  const openSerienDlg = () => {
    setSerieName("");
    setSeriePlatzNummer(1); setSerieWeekday(1); setSerieHours([]); setSerieFromDate(""); setSerieToDate(""); setSerieUserIds([]);
    setSerienDialogOpen(true);
  };
  const handleCreateSeries = async () => {
    if (!seriePlatzNummer || !serieWeekday || serieHours.length===0 || !serieFromDate || !serieToDate || serieUserIds.length === 0)
      return;
    await seriesApi.create({
      name: serieName,
      place: seriePlatzNummer,
      weekday: serieWeekday,
      hours: serieHours.map(Number),    // List of hours
      users: serieUserIds,
      fromDate: serieFromDate,
      toDate: serieToDate
    });
    setSerienDialogOpen(false);
    setTrigger(t => t + 1);
  };
  const handleDeleteSeries = async (seriesId) => {
    await seriesApi.remove(seriesId);
    setTrigger(t => t + 1);
  };

  // Future-only Bookings
  const now = dayjs();
  const futureBookings = bookings.filter(b =>
    b.date && (b.date > now.format("YYYY-MM-DD") ||
      (b.date === now.format("YYYY-MM-DD") && b.hour > now.hour()))
  );

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      {!showDashboard && (
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setShowDashboard(true)}
          >
            Admin-Dashboard öffnen
          </Button>
        </Box>
      )}
      <Collapse in={showDashboard}>
        <Typography variant="h4" sx={{ mb: 2, mt: 2 }}>Admin-Dashboard</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Die Rangliste synchronisiert sich automatisch mit allen freigegebenen Mitgliedern und den eingetragenen Spielergebnissen.
        </Typography>

        {/* Nutzerverwaltung */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
               onClick={() => setUsersOpen(o => !o)}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Nutzerverwaltung</Typography>
            <IconButton>{usersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={usersOpen}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} sx={{ my: 2 }}>
              <Chip color="success" variant="outlined" label={`Spielberechtigt: ${users.filter(u => u.approved && (u.active || u.isAdmin)).length}`} />
              <Chip color="warning" variant="outlined" label={`Inaktiv: ${users.filter(u => u.approved && !u.active && !u.isAdmin).length}`} />
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" color="warning" onClick={handleSeasonReset}>Neue Saison: alle inaktiv setzen</Button>
            </Stack>
            {userMessage && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUserMessage("")}>{userMessage}</Alert>}
            <TableContainer component={Paper} sx={{ mb: 3, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rangliste</TableCell>
                    <TableCell>Freigeschaltet</TableCell>
                    <TableCell>Spielberechtigt (Beitrag bezahlt)</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell>{u.firstname} {u.lastname}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Select size="small" value={u.gender || ""} displayEmpty onChange={e => handleSetGender(u.id, e.target.value)} sx={{ minWidth: 110 }}>
                          <MenuItem value=""><em>Zuordnen</em></MenuItem>
                          <MenuItem value="female">Damen</MenuItem>
                          <MenuItem value="male">Herren</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {u.approved ? "Ja" : "Nein"}
                        {!u.approved && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleApprove(u.id)}
                            sx={{ ml: 2 }}
                          >
                            Freischalten
                          </Button>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Switch
                          checked={!!u.active}
                          color="success"
                          onChange={e => handleSetActive(u.id, e.target.checked)}
                          inputProps={{ "aria-label": "Spielberechtigt" }}
                        />
                        {u.active ? "Aktiv" : "Inaktiv"}
                      </TableCell>
                      <TableCell>
                        {u.isAdmin ? "🛡️ Ja" : "Nein"}
                        <Button
                          variant="outlined"
                          color={u.isAdmin ? "warning" : "primary"}
                          size="small"
                          sx={{ ml: 2 }}
                          onClick={() => handleSetAdmin(u.id, !u.isAdmin)}
                        >
                          {u.isAdmin ? "Admin entfernen" : "Zum Admin machen"}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => handleResetLink(u)}
                        >Passwort-Link</Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteUser(u.id)}
                        >Löschen</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
        {/* Serientermine */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setSeriesOpen(o => !o)}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Serientermine</Typography>
            <IconButton>{seriesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={seriesOpen}>
            <Button variant="contained" color="primary" sx={{ my: 2 }} onClick={openSerienDlg}>
              Serientermin hinzufügen
            </Button>
            <TableContainer component={Paper} sx={{ mb: 3, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Platz</TableCell>
                    <TableCell>Wochentag</TableCell>
                    <TableCell>Stunden</TableCell>
                    <TableCell>Von</TableCell>
                    <TableCell>Bis</TableCell>
                    <TableCell>Teilnehmer</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {series.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>{s.name || "—"}</TableCell>
                      <TableCell>{s.place}</TableCell>
                      <TableCell>
                        {["So","Mo","Di","Mi","Do","Fr","Sa"][s.weekday]}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(s.hours)
                          ? s.hours.map(h=>`${h}:00`).join(", ")
                          : `${s.hour}:00`}
                      </TableCell>
                      <TableCell>{s.fromDate}</TableCell>
                      <TableCell>{s.toDate}</TableCell>
                      <TableCell>
                        {s.users.map(uid =>
                          {
                            const u = users.find(user => user.id === uid);
                            return u ? `${u.firstname} ${u.lastname}` : uid;
                          }
                        ).join(", ")}
                      </TableCell>
                      <TableCell>
                        <Button variant="outlined" color="error" size="small"
                          onClick={() => handleDeleteSeries(s.id)}>
                          Löschen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
        {/* Reservierungen */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
               onClick={() => setBookingsOpen(o => !o)}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Alle zukünftigen Reservierungen</Typography>
            <IconButton>{bookingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={bookingsOpen}>
            <Button variant="contained" sx={{ my:2 }} onClick={openBookingDlg}>
              Für Benutzer buchen
            </Button>
            <TableContainer component={Paper} sx={{ mb: 3, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name/Email</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Stunde</TableCell>
                    <TableCell>Platz</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {futureBookings
                    .sort((a,b) => {
                      if (!a.date || !b.date) return 0;
                      const dateCmp = a.date.localeCompare(b.date);
                      if (dateCmp !== 0) return dateCmp;
                      return a.hour - b.hour;
                    })
                    .map(b => {
                      const user = users.find(u => u.id === b.userId);
                      const userInfo = user ? `${user.firstname || ""} ${user.lastname || ""} (${user.email})` : b.userId;
                      return (
                        <TableRow key={b.id}>
                          <TableCell>{userInfo}</TableCell>
                          <TableCell>{b.date}</TableCell>
                          <TableCell>{`${b.hour}:00 - ${b.hour+1}:00`}</TableCell>
                          <TableCell>{b.platzNummer}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleDeleteBooking(b.id)}
                            >Stornieren</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
        {/* Dialog Passwort-Link */}
        <Dialog open={!!resetLink} onClose={() => setResetLink(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Passwort-Link für {resetLink?.user.firstname} {resetLink?.user.lastname}</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Schicke diesen Link an das Mitglied (z. B. per WhatsApp oder E-Mail). Damit kann ein neues Passwort festgelegt werden.
              Der Link ist {resetLink?.expiresInHours} Stunden gültig und nur einmal verwendbar.
            </Typography>
            <TextField value={resetLink?.link || ""} fullWidth multiline InputProps={{ readOnly: true }} onFocus={e => e.target.select()} />
            {linkCopied && <Alert severity="success" sx={{ mt: 2 }}>Link kopiert.</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetLink(null)}>Schließen</Button>
            <Button variant="contained" onClick={copyResetLink}>Link kopieren</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog Seri Buchung */}
        <Dialog open={serienDialogOpen} onClose={()=>setSerienDialogOpen(false)}>
          <DialogTitle>Serientermin anlegen</DialogTitle>
          <DialogContent>
            <TextField
              label="Serientermin-Name"
              value={serieName}
              onChange={e => setSerieName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb:2 }}>
              <InputLabel>Platz</InputLabel>
              <Select value={seriePlatzNummer} label="Platz" onChange={e=>setSeriePlatzNummer(Number(e.target.value))}>
                <MenuItem value={1}>Platz 1</MenuItem>
                <MenuItem value={2}>Platz 2</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb:2 }}>
              <InputLabel>Wochentag</InputLabel>
              <Select value={serieWeekday} label="Wochentag" onChange={e=>setSerieWeekday(Number(e.target.value))}>
                <MenuItem value={0}>So</MenuItem>
                <MenuItem value={1}>Mo</MenuItem>
                <MenuItem value={2}>Di</MenuItem>
                <MenuItem value={3}>Mi</MenuItem>
                <MenuItem value={4}>Do</MenuItem>
                <MenuItem value={5}>Fr</MenuItem>
                <MenuItem value={6}>Sa</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb:2 }}>
              <InputLabel>Stunden (8–22)</InputLabel>
              <Select
                multiple
                value={serieHours}
                onChange={e=>setSerieHours(e.target.value)}
                input={<OutlinedInput label="Stunden (8-22)" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
              >
                {Array.from({ length: 15 }, (_, i) => 8 + i).map(hour => (
                  <MenuItem key={hour} value={hour}>
                    <Checkbox checked={serieHours.indexOf(hour) > -1} />
                    <ListItemText primary={`${hour}:00 - ${hour + 1}:00`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Von Datum" type="date" value={serieFromDate} onChange={e=>setSerieFromDate(e.target.value)} fullWidth sx={{ mb:2 }} />
            <TextField label="Bis Datum" type="date" value={serieToDate} onChange={e=>setSerieToDate(e.target.value)} fullWidth sx={{ mb:2 }} />
            <FormControl fullWidth>
              <InputLabel>Teilnehmer</InputLabel>
              <Select
                multiple
                value={serieUserIds}
                onChange={e=>setSerieUserIds(e.target.value)}
                input={<OutlinedInput label="Teilnehmer" />}
                renderValue={(selected) => selected.map(uid => {
                  const u = users.find(user => user.id === uid);
                  return u ? `${u.firstname} ${u.lastname}` : uid;
                }).join(", ")}
                MenuProps={MenuProps}
              >
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    <Checkbox checked={serieUserIds.indexOf(u.id) > -1} />
                    <ListItemText primary={`${u.firstname} ${u.lastname} (${u.email})`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setSerienDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" onClick={handleCreateSeries}>{'Speichern'}</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog Einzelbuchung */}
        <Dialog open={bookingDialogOpen} onClose={()=>setBookingDialogOpen(false)}>
          <DialogTitle>Reservierung für Benutzer anlegen</DialogTitle>
          <DialogContent>
            <Select
              value={bookingUser}
              onChange={e => setBookingUser(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Benutzer auswählen</MenuItem>
              {users.map(u => (
                <MenuItem value={u.id} key={u.id}>
                  {u.firstname} {u.lastname} ({u.email})
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="Datum"
              type="date"
              value={datum}
              onChange={e => setDatum(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Stunde (z.B. 8 für 8 Uhr)"
              type="number"
              value={hour}
              onChange={e => setHour(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ min: 8, max: 22 }}
            />
            <Select
              value={platzNummer}
              onChange={e => setPlatzNummer(Number(e.target.value))}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value={1}>Platz 1</MenuItem>
              <MenuItem value={2}>Platz 2</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBookingDialogOpen(false)}>Abbrechen</Button>
            <Button variant="contained" onClick={handleCreateBooking}>Reservieren</Button>
          </DialogActions>
        </Dialog>
      </Collapse>
    </Box>
  );
}